import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { organDonorService, OrganDonorRec } from '../../../../services/organDonorInfo';
import { vaultService } from '../../../../services/vaultService';
import { tasksService } from '../../../../services/tasks';

import { createLogger } from '../../../../utils/logger';
import { ensureCredentialWriteKey } from '../../../../services/encryption/credentialFields';
const logger = createLogger('OrganDonorInput');

type OrganDonorInputScreenRouteProp = RouteProp<RootStackParamList, 'OrganDonorInput'>;

export const OrganDonorInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<OrganDonorInputScreenRouteProp>();
  const { organDonorData, isEditing = false } = route.params || {};

  const [isSaving, setIsSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [hasOnlinePortal, setHasOnlinePortal] = useState(false);
  const [showUsername, setShowUsername] = useState(false);
  const [formData, setFormData] = useState({
    isOrganDonor: organDonorData?.isOrganDonor || false,
    isRegisteredDonor: organDonorData?.isRegisteredDonor || false,
    interestedInBecomingDonor: organDonorData?.interestedInBecomingDonor || false,
    donorRegistry: organDonorData?.donorRegistry || '',
    organsTissues: organDonorData?.organsTissues || '',
    restrictions: organDonorData?.restrictions || '',
    emergencyContact: organDonorData?.emergencyContact || '',
    contactPhone: organDonorData?.contactPhone || '',
    notes: organDonorData?.notes || '',
    portalUsername: '',
    portalPassword: '',
  });

  useEffect(() => {
    if (isEditing) {
      loadVaultCredentials();
    }
  }, [isEditing]);

  const loadVaultCredentials = async () => {
    try {
      if (organDonorData?.id) {
        const portalData = await vaultService.getPortalCredentials(organDonorData.id, 'organ-donor');
        if (portalData) {
          setFormData(prev => ({
            ...prev,
            portalUsername: portalData.username || '',
            portalPassword: portalData.password || '',
          }));
          setHasOnlinePortal(true);
        }
      }
    } catch (error) {
      logger.error('Error loading vault credentials:', error);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    // K5: a login is saved only under a Recovery Code; ask before anything is written.
    if ((formData.portalUsername || formData.portalPassword) && !(await ensureCredentialWriteKey())) {
      setIsSaving(false);
      return;
    }
    try {
      const saveData = {
        isOrganDonor: formData.isOrganDonor,
        isRegisteredDonor: formData.isRegisteredDonor,
        interestedInBecomingDonor: formData.interestedInBecomingDonor,
        donorRegistry: formData.donorRegistry,
        organsTissues: formData.organsTissues,
        restrictions: formData.restrictions,
        emergencyContact: formData.emergencyContact,
        contactPhone: formData.contactPhone,
        notes: formData.notes,
        taskId: 12,
        visibilityState: 'LOCKED' as const,
      };

      let savedRecord: OrganDonorRec;
      if (isEditing && organDonorData?.id) {
        const response = await organDonorService.updateOrganDonor(organDonorData.id, saveData);
        // Handle wrapped response format { success, data, message }
        savedRecord = (response as any)?.data || response;
      } else {
        const response = await organDonorService.createOrganDonor(saveData);
        // Handle wrapped response format { success, data, message }
        savedRecord = (response as any)?.data || response;
      }

      // Store portal credentials in Vault if provided
      if (savedRecord.id && (formData.portalUsername || formData.portalPassword)) {
        try {
          await vaultService.storePortalCredentials(savedRecord.id, 'organ-donor', {
            username: formData.portalUsername,
            password: formData.portalPassword,
          });
        } catch (e) {
          logger.error('Vault write failed for organ donor portal credentials:', e);
        }
      }

      await tasksService.completeTask('organ_donation');

      navigation.navigate('OrganDonorLockUnlock', {
        organDonorId: savedRecord.id,
      });
    } catch (error) {
      logger.error('Error saving organ donor info:', error);
      Alert.alert('Error', 'Failed to save organ donor information. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddAnother = () => {
    navigation.navigate('OrganDonorInput');
  };

  const formatPhoneNumber = (phoneNumber: string) => {
    const cleaned = phoneNumber.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
    return phoneNumber;
  };

  const handlePhoneChange = (text: string) => {
    const cleaned = text.replace(/\D/g, '');
    if (cleaned.length <= 10) {
      const formatted = formatPhoneNumber(cleaned);
      setFormData({ ...formData, contactPhone: formatted });
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>{isEditing ? 'Edit Organ Donor Info' : 'Add Organ Donor Info'}</Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollViewContent} keyboardShouldPersistTaps="handled" automaticallyAdjustKeyboardInsets={true}>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Donor Registry</Text>
            <TextInput
              style={styles.input}
              value={formData.donorRegistry}
              onChangeText={(text) => setFormData({ ...formData, donorRegistry: text })}
              placeholder="Enter donor registry name..."
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Organs/Tissues to Donate</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={formData.organsTissues}
              onChangeText={(text) => setFormData({ ...formData, organsTissues: text })}
              placeholder="Specify which organs/tissues you wish to donate..."
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={4}
              textAlignVertical="top"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Restrictions or Special Instructions</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={formData.restrictions}
              onChangeText={(text) => setFormData({ ...formData, restrictions: text })}
              placeholder="Enter any restrictions or special instructions..."
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={4}
              textAlignVertical="top"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Emergency Contact Name</Text>
            <TextInput
              style={styles.input}
              value={formData.emergencyContact}
              onChangeText={(text) => setFormData({ ...formData, emergencyContact: text })}
              placeholder="Enter emergency contact name..."
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Emergency Contact Phone</Text>
            <TextInput
              style={styles.input}
              value={formData.contactPhone}
              onChangeText={handlePhoneChange}
              placeholder="(555) 123-4567"
              placeholderTextColor="#A9A9A9"
              keyboardType="phone-pad"
            />
          </View>

          {/* Portal Toggle */}
          <View style={styles.inputGroup}>
            <TouchableOpacity
              style={styles.toggleContainer}
              onPress={() => setHasOnlinePortal(!hasOnlinePortal)}
            >
              <View style={[styles.toggle, hasOnlinePortal && styles.toggleActive]}>
                <View style={[styles.toggleThumb, hasOnlinePortal && styles.toggleThumbActive]} />
              </View>
              <Text style={styles.toggleLabel}>Does your organ donor registry have an online portal?</Text>
            </TouchableOpacity>
            <Text style={styles.helperText}>
              If yes, enter your login credentials. These will be securely stored in your vault and will only be shared with your successor after your End of Life event.
            </Text>
          </View>

          {hasOnlinePortal && (
            <>
              <View style={styles.inputGroup}>
                <Text style={styles.label}>Portal Username 🔒</Text>
                <View style={styles.passwordContainer}>
                  <TextInput
                    style={[styles.input, styles.passwordInput]}
                    value={formData.portalUsername}
                    onChangeText={(text) => setFormData({ ...formData, portalUsername: text })}
                    placeholder="Enter Username"
                    placeholderTextColor="#A9A9A9"
                    secureTextEntry={!showUsername}
                    autoCapitalize="none"
                  />
                  <TouchableOpacity
                    style={styles.eyeButton}
                    onPress={() => setShowUsername(!showUsername)}
                  >
                    <Image
                      source={showUsername ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                      style={styles.eyeIcon}
                    />
                  </TouchableOpacity>
                </View>
                <Text style={styles.vaultText}>Stored securely - shared only after EOL</Text>
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Portal Password 🔒</Text>
                <View style={styles.passwordContainer}>
                  <TextInput
                    style={[styles.input, styles.passwordInput]}
                    value={formData.portalPassword}
                    onChangeText={(text) => setFormData({ ...formData, portalPassword: text })}
                    placeholder="Enter Password"
                    placeholderTextColor="#A9A9A9"
                    secureTextEntry={!showPassword}
                    autoCapitalize="none"
                  />
                  <TouchableOpacity
                    style={styles.eyeButton}
                    onPress={() => setShowPassword(!showPassword)}
                  >
                    <Image
                      source={showPassword ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                      style={styles.eyeIcon}
                    />
                  </TouchableOpacity>
                </View>
                <Text style={styles.vaultText}>Stored securely - shared only after EOL</Text>
              </View>
            </>
          )}

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Additional Notes</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={formData.notes}
              onChangeText={(text) => setFormData({ ...formData, notes: text })}
              placeholder="Enter any additional notes..."
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={4}
              textAlignVertical="top"
            />
          </View>

          <View style={styles.buttonContainer}>
            {!isEditing && (
              <TouchableOpacity
                style={[styles.button, styles.addAnotherButton]}
                onPress={handleAddAnother}
                disabled={isSaving}
              >
                <Text style={styles.buttonText}>Add Another</Text>
              </TouchableOpacity>
            )}

            <TouchableOpacity
              style={[styles.button, styles.saveButton]}
              onPress={handleSave}
              disabled={isSaving}
            >
              {isSaving ? (
                <ActivityIndicator color="#FFFFFF" size="small" />
              ) : (
                <Text style={styles.buttonText}>{isEditing ? 'Update' : 'Save'}</Text>
              )}
            </TouchableOpacity>
          </View>
          <View style={styles.bottomPadding} />
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(50),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
  },
  backgroundKey: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    opacity: 0.1,
  },
  logo: {
    width: s(120),
    height: s(120),
    marginBottom: ms(16),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
  },
  scrollView: {
    paddingHorizontal: ms(16),
  },
  scrollViewContent: {
    paddingBottom: ms(350),
  },
  inputGroup: {
    marginBottom: ms(16),
  },
  label: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#6B7280',
    marginBottom: ms(8),
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(25),
    padding: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  textArea: {
    minHeight: s(100),
    height: 'auto',
    textAlignVertical: 'top',
    paddingTop: ms(15),
    paddingHorizontal: ms(15),
  },
  yesNoContainer: {
    flexDirection: 'row',
    gap: ms(10),
  },
  yesNoButton: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderRadius: ms(25),
    padding: ms(15),
    alignItems: 'center',
    borderWidth: ms(2),
    borderColor: '#d8dbdd',
  },
  yesNoButtonSelected: {
    backgroundColor: '#29BFC0',
    borderColor: '#29BFC0',
  },
  yesNoButtonText: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#6B7280',
  },
  yesNoButtonTextSelected: {
    color: '#FFFFFF',
  },
  toggleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: ms(10),
  },
  toggle: {
    width: s(50),
    height: s(30),
    borderRadius: ms(15),
    backgroundColor: '#E5E7EB',
    marginRight: ms(15),
    padding: ms(2),
    justifyContent: 'center',
  },
  toggleActive: {
    backgroundColor: '#29BFC0',
  },
  toggleThumb: {
    width: s(26),
    height: s(26),
    borderRadius: ms(13),
    backgroundColor: '#FFFFFF',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
    elevation: 2,
  },
  toggleThumbActive: {
    alignSelf: 'flex-end',
  },
  toggleLabel: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    flex: 1,
  },
  helperText: {
    fontSize: ms(14, 0.3),
    color: '#FF9F5A',
    marginTop: ms(5),
    lineHeight: ms(18, 0.3),
  },
  vaultText: {
    fontSize: ms(14, 0.3),
    color: '#FF9F5A',
    marginTop: ms(5),
    fontWeight: '600',
  },
  passwordContainer: {
    position: 'relative',
  },
  passwordInput: {
    paddingRight: ms(50),
  },
  eyeButton: {
    position: 'absolute',
    right: ms(15),
    top: ms(15),
    padding: ms(5),
  },
  eyeIcon: {
    width: s(20),
    height: s(20),
    tintColor: '#6B7280',
  },
  buttonContainer: {
    flexDirection: 'row',
    gap: ms(10),
    paddingVertical: ms(20),
    paddingHorizontal: ms(16),
  },
  button: {
    flex: 1,
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  addAnotherButton: {
    backgroundColor: '#FF9F5A',
  },
  saveButton: {
    backgroundColor: '#29BFC0',
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  bottomPadding: {
    height: s(120),
  },
});
